import React, { useCallback, useEffect, useState } from 'react';
import { AppState } from '$/store';
import { connect } from 'react-redux';
import Loading from '$/components/loading/Loading';
import * as Bixby from '$/core';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi } from 'ag-grid-enterprise';
import NoDataFound from '$/components/global/noData';
import { getAllCloDealHoldingForBixbyIdMappings } from '$/store/cloDealHoldingBixybyIdMapping/actions';
import { CloDealService } from '$/services';
import BixButton from '$/components/global/bixButton';
import './index.scss';

interface ICloDealMappingListProps {
  isLoading: boolean;
  error?: Bixby.Models.BixbyError;
  cloDealHoldingForBixbyIdMappings: Bixby.Models.CloDealHoldingForBixbyIdMapping[] | undefined;
  dispatchGetCloDealHoldingForBixbyIdMappings: () => Promise<void>;
}

type CloDealHoldingForBixbyIdMissingMapping = Bixby.Models.CloDealHoldingForBixbyIdMapping & {
  selectedBixbyId?: string | null | undefined;
  isMappedComposite?: string | null | undefined;
};

const createPayLoad = (originalMappings, updatedMappings) => {
  const holdingsToSave: Bixby.Models.CloDealBixbyIdMapInput[] = [];
  originalMappings?.forEach(x => {
    const currentHolding = updatedMappings?.find(y => y.id === x.id);

    if (currentHolding && currentHolding.isMapped !== x.isMapped) {
      holdingsToSave.push({
        id: x.id,
        instrumentId: x.instrumentId,
        instrumentName: x.instrumentName,
        bixbyId: currentHolding.selectedBixbyId,
        issuerName: currentHolding.issuerName,
        figiTicker: currentHolding.figiTicker,
        isMapped: currentHolding.isMapped,
      });
    }
  });
  return holdingsToSave;
};

const CloDealMissingMappingList: React.FC<ICloDealMappingListProps> = props => {
  const { cloDealHoldingForBixbyIdMappings, isLoading, dispatchGetCloDealHoldingForBixbyIdMappings } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [currentCloDealHoldingForBixbyIdMappings, setCurrentCloDealHoldingForBixbyIdMappings] = useState(
    undefined as CloDealHoldingForBixbyIdMissingMapping[] | undefined
  );
  const [toggleChecked, setToggleChecked] = useState(false);
  const [hasAutoMapping, setHasAutoMapping] = useState(false);
  const [savingUpdatedMappings, setSavingUpdatedMappings] = useState(false);

  useEffect(() => {
    const getCloDispatcher = async () => {
      await dispatchGetCloDealHoldingForBixbyIdMappings();
    };
    getCloDispatcher();
  }, [dispatchGetCloDealHoldingForBixbyIdMappings]);

  useEffect(() => {
    const newCloDealHoldingForBixbyIdMappingState = (cloDealHoldingForBixbyIdMappings ?? []).map(x => ({
      ...x,
      isMapped: !x.flaggedForManualReview,
      selectedBixbyId: !x.flaggedForManualReview && x.companies.length > 0 ? x?.companies[0].bixbyId : null,
      isMappedComposite: `${!x.flaggedForManualReview}--${
        !x.flaggedForManualReview && x.companies.length > 0 ? x?.companies[0].bixbyId : ''
      }`,
    }));
    setHasAutoMapping(newCloDealHoldingForBixbyIdMappingState.some(x => !x.flaggedForManualReview));
    setCurrentCloDealHoldingForBixbyIdMappings(newCloDealHoldingForBixbyIdMappingState);
    setToggleChecked(!!newCloDealHoldingForBixbyIdMappingState.find(x => x.isMapped));
  }, [cloDealHoldingForBixbyIdMappings]);

  const handleToggleMapping = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setCurrentCloDealHoldingForBixbyIdMappings(
          (cloDealHoldingForBixbyIdMappings ?? []).map(x => ({
            ...x,
            isMapped: false,
            selectedBixbyId: null,
            isMappedComposite: `${false}--${''}`,
          }))
        );
        setToggleChecked(false);
        return;
      }

      const newCurrentCloDealHoldingsForBixbyIdMappings = (cloDealHoldingForBixbyIdMappings ?? []).map(x => ({
        ...x,
        isMapped: !x.flaggedForManualReview,
        selectedBixbyId: !x.flaggedForManualReview && x.companies.length > 0 ? x?.companies[0].bixbyId : null,
        isMappedComposite: `${!x.flaggedForManualReview}--${
          !x.flaggedForManualReview && x.companies.length > 0 ? x?.companies[0].bixbyId : ''
        }`,
      }));

      setCurrentCloDealHoldingForBixbyIdMappings(newCurrentCloDealHoldingsForBixbyIdMappings);
      setToggleChecked(!!newCurrentCloDealHoldingsForBixbyIdMappings.find(x => x.isMapped));
    },
    [cloDealHoldingForBixbyIdMappings]
  );

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const handleSave = useCallback(async () => {
    setSavingUpdatedMappings(true);
    const holdingsToSave = createPayLoad(cloDealHoldingForBixbyIdMappings, currentCloDealHoldingForBixbyIdMappings);

    if (holdingsToSave.length > 0) {
      await CloDealService.addCloBixbyIdMappings(holdingsToSave);
      await dispatchGetCloDealHoldingForBixbyIdMappings();
      setSavingUpdatedMappings(false);
    } else {
      setSavingUpdatedMappings(false);
    }
  }, [
    dispatchGetCloDealHoldingForBixbyIdMappings,
    cloDealHoldingForBixbyIdMappings,
    currentCloDealHoldingForBixbyIdMappings,
  ]);

  const isMappedRenderer = (params: any) => {
    return (params.data.companies ?? []).map(({ id, bixbyId }) => (
      <div
        key={`mapping_container_${params?.data.id}_company_${id}_clo_mapping_${bixbyId}`}
        id={`mapping_container_${params?.data.id}_company_${id}_clo_mapping_${bixbyId}`}>
        <input
          type="checkbox"
          checked={bixbyId === params.data.selectedBixbyId && params.data.isMapped === true}
          key={`mapping_checkbox_${params?.data.id}_company_${id}_clo_mapping_${bixbyId}`}
          id={`mapping_checkbox_${params?.data.id}_company_${id}_clo_mapping_${bixbyId}`}
          onChange={e => {
            params.data.isMapped = e.target.checked;
            params.data.selectedBixbyId = bixbyId;
            params.data.isMappedComposite = `${params.data.isMapped}--${params.data.selectedBixbyId}`;
            params.api.refreshCells();
          }}
        />
      </div>
    ));
  };

  const loading = isLoading || savingUpdatedMappings;

  if (loading) {
    return <Loading pageLevel />;
  } else {
    return (
      <section className="clo_list">
        {currentCloDealHoldingForBixbyIdMappings &&
        !Bixby.Utils.isNullOrEmpty(currentCloDealHoldingForBixbyIdMappings) ? (
          <div className="clo_list_datagrid">
            <div className="grid_container">
              {(hasAutoMapping || toggleChecked) && (
                <div style={{ justifyItems: 'right' }}>
                  <div>
                    <BixButton light useSmall onClick={() => handleToggleMapping(!toggleChecked)}>
                      {toggleChecked ? 'Uncheck all' : 'Check all auto mappings'}
                    </BixButton>
                  </div>
                </div>
              )}
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                    filter: 'number',
                    cellClass: 'align-center',
                  }}
                  onGridReady={onGridReady}
                  rowData={currentCloDealHoldingForBixbyIdMappings}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  animateRows={true}
                  rowHeight={35}
                  headerHeight={35}
                  components={{
                    isMappedRenderer: isMappedRenderer,
                  }}
                  columnDefs={[
                    {
                      field: 'instrumentId',
                      headerName: 'Instrument Id',
                      width: 120,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'instrumentName',
                      headerName: 'Asset',
                      width: 200,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      valueGetter: props => `${props.data?.issuerName ?? ''} - ${props.data?.instrumentName ?? ''}`,
                      floatingFilter: true,
                    },
                    {
                      field: 'figiTicker',
                      headerName: 'Figi Ticker',
                      width: 120,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                    },
                    {
                      field: 'companies',
                      headerName: 'Bixby IDs',
                      width: 500,
                      autoHeight: true,
                      cellClass: 'align-left',
                      filter: 'agTextColumnFilter',
                      floatingFilter: true,
                      valueGetter: (params: any) => {
                        return (params?.data.companies ?? [])
                          .map((company: Bixby.Models.AdminCompanySlim) => `${company?.bixbyId}`)
                          .join(';');
                      },
                      cellRenderer: (params: any) => {
                        const { id } = params?.data ?? {};

                        return (params?.data.companies ?? []).map((company: Bixby.Models.AdminCompanySlim) => (
                          <div key={`${id}-${company.id}`} id={`${id}-${company.id}`} style={{ marginBottom: 2 }}>
                            {`${company?.bixbyId}`}
                          </div>
                        ));
                      },
                    },
                    {
                      field: 'isMappedComposite',
                      headerName: 'Mapping',
                      width: 80,
                      autoHeight: true,
                      filter: '',
                      cellClass: 'align-left',
                      cellRenderer: 'isMappedRenderer',
                    },
                    {
                      field: 'flaggedForManualReview',
                      headerName: 'Status',
                      width: 80,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                      editable: false,
                      valueGetter: (params: any) => {
                        return params?.data?.flaggedForManualReview ? 'Review' : '';
                      },
                    },
                    {
                      field: 'reviewNote',
                      headerName: 'Note',
                      width: 80,
                      filter: 'agTextColumnFilter',
                      cellClass: 'align-left',
                      floatingFilter: true,
                      editable: false,
                      valueGetter: (params: any) => {
                        if (
                          params?.data?.reviewNote ===
                          Bixby.Enums.CloDealHoldingForBixbyIdMappingReviewNote.MultipleBixbyIds
                        ) {
                          return 'Multiple IDs';
                        }
                        if (
                          params?.data?.reviewNote ===
                          Bixby.Enums.CloDealHoldingForBixbyIdMappingReviewNote.RBC_Barclays
                        ) {
                          return 'RBC / Barclays';
                        }

                        return '';
                      },
                    },
                  ]}></AgGridReact>
              </div>
            </div>
            <BixButton onClick={handleSave}>Save</BixButton>
          </div>
        ) : (
          <NoDataFound dataType="clo mappings" />
        )}
      </section>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.cloDealHoldingBixbyIdMappingState.isLoading,
    error: state.cloDealHoldingBixbyIdMappingState.error,
    cloDealHoldingForBixbyIdMappings: state.cloDealHoldingBixbyIdMappingState.cloDealHoldingForBixbyIdMappings,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCloDealHoldingForBixbyIdMappings: () => dispatch(getAllCloDealHoldingForBixbyIdMappings()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CloDealMissingMappingList);
